// Canvas widget adapters (TASK-494). Each wraps an existing self-fetching Kielis
// component in `WidgetContainer` so it gains the provenance badge + tile chrome —
// reuse, don't rebuild. Registered under WIDGET-INVENTORY catalog names in
// registry.ts (those names are the allowlist the UI brain, TASK-499, picks from).
//
// Client-scoped widgets receive ONLY { clientId } (server-injected, TASK-499) — the
// adapter forwards clientId explicitly, never the rest of spec.props. Book/compliance
// widgets take nothing (account rides the X-Account-ID header in lib/httpClient).
//
// Widgets that already render their own title pass `source` only (no adapter title)
// to avoid a double-title. ponytail: thin wrappers live in one file, not 11 — the
// bigger filterable widgets (ClientBook, TASK-495) get their own files in this dir.
import { useState } from 'react';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetFilterBar } from '../WidgetFilterBar';
import { PortfolioHoldingsSpreadsheet } from './OpenAsSpreadsheet';
import DnaCard, { type DnaCardFilters } from '../../dna/DnaCard';
import NotesTab from '../../ClientProfile/tabs/NotesTab';
import DocumentsTab from '../../ClientProfile/tabs/DocumentsTab';
import PortfoliosTab from '../../ClientProfile/tabs/PortfoliosTab';
import MinIODocumentsSection from '../../documents/MinIODocumentsSection';
import DocumentComplianceDashboard from '../../documents/DocumentComplianceDashboard';
import ComplianceStatusPanel from '../../compliance/ComplianceStatusPanel';
import QuarantineQueue from '../../documents/QuarantineQueue';
import ProposalsList from '../../portfolio/ProposalsList';
import PortfolioView from '../../portfolio/PortfolioView';
import COReviewQueue, { type StatusFilter } from '../../compliance/review/COReviewQueue';
import COReviewWorkspace from '../../compliance/review/COReviewWorkspace';
import { useCanvasStore } from '../../../stores/canvasStore';
import { useFocusStore } from '../../../stores/focusStore';
import RegulatoryFeedTab from '../../compliance-page/RegulatoryFeedTab';
// TASK-507: expand the live self-fetch set — all take an id or nothing, real data.
import AllDocumentsView from '../../documents/AllDocumentsView';
import SecureLinksTable from '../../documents/SecureLinksTable';
import DocumentRequestList from '../../compliance/review/DocumentRequestList';
import DocumentAnalysisTab from '../../compliance-page/DocumentAnalysisTab';
// TASK-511: canonical PDF viewer + timeline for the canvas (both self-fetch from clientId).
import DocumentViewerPanel from '../../kyc/workspace/DocumentViewerPanel';
import { ActivityTimeline } from '../../compliance/overview/ActivityTimeline';
// TASK-512: bucket A (self-fetch) + the clientName-scope tabs (KYC/Risk read the injected name).
import { AIUsageStats } from '../../admin/ai/AIUsageStats';
import VoiceNoteWidget from '../../dna/VoiceNoteWidget';
import KYCTab from '../../ClientProfile/tabs/KYCTab';
import RiskProfileTab from '../../ClientProfile/tabs/RiskProfileTab';
// TASK-575 redesign: meeting action items on the canvas. Reuses the existing
// client-task query + TaskCard — no new service/endpoint.
import { useClientTasks } from '../../../hooks/useTasksQuery';
import TaskCard from '../../tasks/TaskCard';

interface ClientScoped {
  clientId: string;
}

// --- client scope: forward ONLY clientId ---------------------------------------

// Filterable (TASK-495 contract): sanitized `filters` seed the display filters;
// the on-tile bar edits them. All display-shaping, applied inside DnaCard.
export const DnaCardWidget = ({
  clientId,
  filters,
}: ClientScoped & { filters?: DnaCardFilters & Record<string, unknown> }) => {
  const [active, setActive] = useState<DnaCardFilters & Record<string, unknown>>(filters ?? {});
  return (
    <WidgetContainer
      title="Client DNA"
      source="Knowledge Graph"
      toolbar={
        <WidgetFilterBar
          component="DnaCard"
          values={active}
          onChange={(partial) => setActive((prev) => ({ ...prev, ...partial }))}
        />
      }
    >
      <DnaCard clientId={clientId} filters={active} />
    </WidgetContainer>
  );
};

export const NotesTabWidget = ({ clientId }: ClientScoped) => (
  <WidgetContainer title="Notes" source="Client Service">
    <NotesTab clientId={clientId} />
  </WidgetContainer>
);

// Client's open action items / follow-up tasks (self-fetch via /api/tasks?client_id=).
export const MeetingActionItemsWidget = ({ clientId }: ClientScoped) => {
  const { data, isLoading } = useClientTasks(clientId);
  const tasks = data?.tasks ?? [];
  return (
    <WidgetContainer title="Action Items" source="Task Service">
      {isLoading ? (
        <div className="p-4 text-sm text-gray-500">Loading action items…</div>
      ) : tasks.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">No open action items for this client.</div>
      ) : (
        <div className="space-y-2 p-2">
          {tasks.map((t) => (
            <TaskCard key={t.id} task={t} isCompact />
          ))}
        </div>
      )}
    </WidgetContainer>
  );
};

export const DocumentsTabWidget = ({ clientId }: ClientScoped) => (
  <WidgetContainer title="Documents" source="Documents">
    <DocumentsTab clientId={clientId} />
  </WidgetContainer>
);

export const MinIODocumentsWidget = ({ clientId }: ClientScoped) => (
  <WidgetContainer title="Documents" source="Documents">
    <MinIODocumentsSection clientId={clientId} />
  </WidgetContainer>
);

export const PortfoliosTabWidget = ({ clientId }: ClientScoped) => (
  <WidgetContainer title="Portfolios" source="Portfolio API">
    <PortfoliosTab clientId={clientId} />
  </WidgetContainer>
);

// ProposalsList renders its own "Portfolio Proposals" h3 → no adapter title.
export const ProposalsListWidget = ({ clientId }: ClientScoped) => (
  <WidgetContainer source="Portfolio API">
    <ProposalsList clientId={clientId} />
  </WidgetContainer>
);

// PortfolioView renders its own section titles → no adapter title.
export const CockpitPortfolioWidget = ({ clientId }: ClientScoped) => (
  // TASK-606: canvas → workbench bridge in the toolbar slot.
  <WidgetContainer source="Portfolio API" toolbar={<PortfolioHoldingsSpreadsheet clientId={clientId} />}>
    <PortfolioView clientId={clientId} />
  </WidgetContainer>
);

// --- book / compliance scope: no props (account via X-Account-ID header) --------
// All four render their own title → source badge only.

// Filterable (TASK-495): the sanitized `filters.status` seeds the initial queue
// filter; COReviewQueue's own on-tile status buttons handle subsequent edits.
// TASK-733: hosted on the canvas, selecting a review opens a WORKSPACE TILE instead of
// navigating the browser away — the queue, the Radar and every surrounding tile stay
// mounted. The full-page route flow is untouched (pages render COReviewQueue directly,
// without this adapter, so they keep navigate()).
export const COReviewQueueWidget = ({ filters }: { filters?: { status?: StatusFilter } }) => {
  const addSpecs = useCanvasStore((s) => s.addSpecs);
  return (
    <WidgetContainer source="Review Service">
      <COReviewQueue
        status={filters?.status}
        onSelectReview={(review) =>
          addSpecs([{
            component: 'COReviewWorkspace',
            // reviewId participates in tile identity (TASK-726/733): the same review
            // focuses its existing tile; two reviews for one client stay two tiles.
            props: { reviewId: review.id },
          }])
        }
      />
    </WidgetContainer>
  );
};

// TASK-733: the review workspace as a canvas tile. Explicit id via props; the same
// component keeps serving /compliance/review/:reviewId through its route fallback.
export const COReviewWorkspaceWidget = ({
  reviewId,
  subjectKey,
}: {
  reviewId?: string;
  subjectKey?: string;
}) => (
  <WidgetContainer source="Review Service">
    <COReviewWorkspace reviewId={reviewId} subjectKey={subjectKey} host="canvas" />
  </WidgetContainer>
);

export const DocumentComplianceWidget = () => (
  <WidgetContainer source="Compliance">
    <DocumentComplianceDashboard />
  </WidgetContainer>
);

// TASK-555: account-level compliance status. Self-fetches the honest summary; renders "No data"
// (gray), never a fake-green pass. Renders its own title → source badge only.
export const ComplianceStatusWidget = () => (
  <WidgetContainer source="Compliance">
    <ComplianceStatusPanel />
  </WidgetContainer>
);

export const QuarantineQueueWidget = () => (
  <WidgetContainer source="Compliance">
    <QuarantineQueue />
  </WidgetContainer>
);

export const RegulatoryFeedWidget = () => (
  <WidgetContainer source="AML Service">
    <RegulatoryFeedTab />
  </WidgetContainer>
);

// --- TASK-507: additional self-fetch Tier-1 widgets (expand the live catalog) ------
// Client-scoped: forward only clientId (SecureLinksTable's onCreateLink is optional —
// the tile lists + copy/resend/revoke without a create modal on the canvas).
export const SecureLinksTableWidget = ({ clientId }: ClientScoped) => (
  <WidgetContainer source="Documents">
    <SecureLinksTable clientId={clientId} />
  </WidgetContainer>
);

export const DocumentRequestListWidget = ({ clientId }: ClientScoped) => (
  <WidgetContainer title="Document Requests" source="Compliance">
    <DocumentRequestList clientId={clientId} />
  </WidgetContainer>
);

// Book scope: no props (account rides X-Account-ID). Both render their own headers.
export const AllDocumentsWidget = () => (
  <WidgetContainer source="Documents">
    <AllDocumentsView />
  </WidgetContainer>
);

export const DocumentAnalysisWidget = () => (
  <WidgetContainer source="AML Service">
    <DocumentAnalysisTab />
  </WidgetContainer>
);

// --- TASK-511: canonical widgets for the PDF-viewer and timeline patterns ----------
// DocumentViewerPanel is client-scoped and self-fetches its doc list + presigned URLs;
// registers as-is (embedded panel, no modal chrome). Canonical over PDFDocumentViewer.
export const DocumentViewerWidget = ({ clientId }: ClientScoped) => (
  <WidgetContainer title="Documents" source="Documents">
    <DocumentViewerPanel clientId={clientId} />
  </WidgetContainer>
);

// ActivityTimeline takes an explicit clientId here (it falls back to ComplianceContext
// on its routed page). Canonical over ActionTimeline (parent-fed) / HistoryTab (mock).
export const ActivityTimelineWidget = ({ clientId }: ClientScoped) => (
  <WidgetContainer title="Activity" source="Compliance">
    <ActivityTimeline clientId={clientId} />
  </WidgetContainer>
);

// --- TASK-512: bucket A + clientName-scope tabs ------------------------------------
// AIUsageStats — book/account scope (no props); account rides the X-Account-ID header.
export const AIUsageStatsWidget = () => (
  <WidgetContainer source="AI Admin">
    <AIUsageStats />
  </WidgetContainer>
);

// VoiceNoteWidget — client scope; clientName is optional (display only). Named
// `VoiceNoteTile` to avoid clashing with the imported component; registry key stays
// `VoiceNoteWidget` (the WIDGET-INVENTORY catalog name).
//
// Deep review #6: this shim dropped `sessionId`. Every canvas entry into dictation passes it —
// the capture chooser's "don't record, dictate a summary after" (TASK-673), and both Dictate
// paths on the meetings list — and `VoiceNoteWidget` uses it for the whole of TASK-668:
// provenance label, `session_id`/`note_source_type` on the note, and the
// `documentMeetingByDictation` call that marks the meeting DOCUMENTED. Dropped here, the third
// capture mode wrote an unbound client note and the meeting stayed undocumented forever —
// the epic's own headline defect, reintroduced by a prop-forwarding wrapper.
export const VoiceNoteTile = ({
  clientId,
  clientName,
  sessionId,
}: ClientScoped & { clientName?: string; sessionId?: string }) => (
  <WidgetContainer title={sessionId ? 'Dictate meeting summary' : 'Voice Note'} source="Voice Notes">
    <VoiceNoteWidget clientId={clientId} clientName={clientName} sessionId={sessionId} />
  </WidgetContainer>
);

// KYC / Risk profile tabs — client scope; consume the server-injected clientName
// (TASK-512). KYCTab requires the prop but never reads it; RiskProfileTab uses it for
// the PDF export filename. `?? ''` guards the dev/no-name path.
export const KYCTabWidget = ({ clientId, clientName }: ClientScoped & { clientName?: string }) => (
  <WidgetContainer title="KYC Profile" source="KYC Service">
    {/* TASK-716: ⤢ opens the full KYC workspace as a focus popup — URL stays /dashboard. */}
    <div className="mb-2 flex justify-end">
      <button
        type="button"
        data-testid="kyc-focus-open"
        title="Open full KYC workspace"
        aria-label="Open full KYC workspace"
        onClick={() =>
          useFocusStore.getState().push({ workspace: 'kyc', entityId: clientId })
        }
        className="rounded-md border border-border px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        Open workspace ⤢
      </button>
    </div>
    <KYCTab clientId={clientId} clientName={clientName ?? ''} />
  </WidgetContainer>
);

export const RiskProfileTabWidget = ({ clientId, clientName }: ClientScoped & { clientName?: string }) => (
  <WidgetContainer title="Risk Profile" source="Risk Profile Service">
    <RiskProfileTab clientId={clientId} clientName={clientName ?? ''} />
  </WidgetContainer>
);
